/***
    === [ CORETEX FYI ] ===
    - Dynamic Imports: The import() function is used within the connectedCallback method 
    to dynamically import the "Autoplay" and "Fade" plugins only if 
    the relevant attributes (autoplay and fade) are present.

    - The connectedCallback method is marked as async 
    to handle the asynchronous import() calls.
*/

import EmblaCarousel from "//hypeairs.de/cdn/shop/t/5/assets/m-carousel.js?v=33832653016626342751743200194"
import ClassNames from "//hypeairs.de/cdn/shop/t/5/assets/h-carousel-cn.js?v=33863959509046118241743200194"

export default class CoretexSlider extends HTMLElement {
        constructor() {
            super();
            this.embla = null;
            this.autoplayPlugin = null; // Store reference to the Autoplay plugin
        }

        // === [ CORETEX FYI ] === 
        // The connectedCallback method is marked as async to handle the asynchronous import() calls.
        // customOptions = {} is used to pass custom options to the sub-classes
        async connectedCallback(customOptions = {}) {
            
            this.emblaNode = this.querySelector('coretex-slider');
            this.viewportNode = this.querySelector('[slider-viewport]');

            this.buttonPrev = this.querySelector('[control-prev]');
            this.buttonNext = this.querySelector('[control-next]');
            this.buttonPlay = this.querySelector('[control-play]');
            this.buttonDots = this.querySelector('[control-dots]');
            this.numbersNode = this.querySelector('[control-numbers]');

            if (!this.viewportNode) {
                console.error('Viewport node is not found.');
                return;
            }

            const plugins = [ClassNames()]; // Always include ClassNames plugin

            // Check for slider-autoplay attribute [plugin]
            if (this.hasAttribute('autoplay')) {
                const delayAttr = this.getAttribute('delay');
                const autoplayDelay = delayAttr ? parseInt(delayAttr, 10) : 4000; // default is set to 4s delay between slides
                const { default: Autoplay } = await import("//hypeairs.de/cdn/shop/t/5/assets/h-carousel-ap.js?v=10249494161621988281743200194"); // conditionally load plugin w/ dynamic imports
                const autoplay = Autoplay({ stopOnInteraction: true, delay: autoplayDelay });
                plugins.push(autoplay);
                this.autoplayPlugin = autoplay; // Store reference to the Autoplay plugin
            }

            // Check for slider-fade attribute [plugin]
            if (this.hasAttribute('fade')) {
                const { default: Fade } = await import("//hypeairs.de/cdn/shop/t/5/assets/h-carousel-fa.js?v=40069100367990318831743200194"); // conditionally load plugin w/ dynamic imports
                plugins.push(Fade());
            }

            // Default slider options
            let OPTIONS = {
                active: true,
                loop: false,
                axis: 'x',
                breakpoints: {
                    '(max-width: 777px)': { active: true, axis: 'x' }  // Mobile active by default
                },
                ...customOptions // === [ CORETEX FYI ] === Merge custom options from sub-classes (example in pdp-slider.js)
            };

            // Get attributes and set options accordingly
            const axis = this.getAttribute('direction'); // x or y
            const axisS = this.getAttribute('direction-s'); // x or y
            const loop = this.getAttribute('loop');
            const mq = this.getAttribute('mq');
            const mqS = this.getAttribute('mq-s');

            // Set the axis if the attribute is present
            if (this.hasAttribute('direction')) {
                OPTIONS.axis = axis;
            }

            // Set the axis for small screens if the attribute is present
            if (this.hasAttribute('direction-s')) {
                OPTIONS.breakpoints['(max-width: 777px)'].axis = axisS;
            }

            // Set the loop if the attribute is present
            if (this.hasAttribute('loop')) {
                OPTIONS.loop = loop === 'true'; 
            }

            // Set the desktop/tablet active status if the attribute is present
            if (this.hasAttribute('mq')) {
                OPTIONS.active = mq === 'true'; // Converts string "true"/"false" to boolean
            }

            // Set the mobile active status if the attribute is present
            if (this.hasAttribute('mq-s')) {
                OPTIONS.breakpoints['(max-width: 777px)'].active = mqS === 'true'; // Converts string "true"/"false" to boolean
            }

            // Initialize EmblaCarousel with options and selected plugins
            this.embla = EmblaCarousel(this.viewportNode, OPTIONS, plugins);

            // Store a reference to the emblaApi
            this.emblaApi = this.embla.internalEngine();
            this.isSliderActive = this.emblaApi.options.active; // Check slider state

            this.init();

            // Shopify editor exclusive
            if (Shopify.designMode && !this.hasAttribute('data-nosdm')) {
                document.addEventListener('shopify:section:load', (event) => filterShopifyEvent(event, this, this.stopAutoplay()));
                document.addEventListener('shopify:section:select', (event) => filterShopifyEvent(event, this, this.stopAutoplay()));

                document.addEventListener('shopify:block:load', (event) => filterShopifyEvent(event, this, this.selectSlide(event)));
                document.addEventListener('shopify:block:load', (event) => filterShopifyEvent(event, this, this.selectSlide(event)));
                document.addEventListener('shopify:block:select', (event) => filterShopifyEvent(event, this, this.selectSlide(event)));
            }
        }

        disconnectedCallback() {
            this.destroy();
        }

        init() {
            this.embla.on('init', this.a11y.bind(this));
            this.embla.on('select', this.a11y.bind(this));

            this.a11y();
            this.buttons();
            this.dots();
            this.numbers();
            this.setupInteractionStopAutoplay();
        }

        destroy() {
            if (this.embla) {
                this.embla.destroy();
                this.embla = null;
            }

            // Shopify editor exclusive
            if (Shopify.designMode && !this.hasAttribute('data-nosdm')) {
                document.removeEventListener('shopify:block:load', (event) => filterShopifyEvent(event, this, this.selectSlide(event)));
                document.removeEventListener('shopify:block:select', (event) => filterShopifyEvent(event, this, this.selectSlide(event)));
            }
        }

        a11y() {
            const slides = this.embla.slideNodes();
            const inView = this.embla.slidesInView();

            slides.forEach((slide, index) => {
                if (inView.includes(index)) {
                    slide.removeAttribute('aria-hidden');
                } else {
                    slide.setAttribute('aria-hidden', 'true');
                }
            });

            // Additional a11y improvements
            this.viewportNode.setAttribute('role', 'region');
            this.viewportNode.setAttribute('aria-roledescription', 'carousel');
            this.viewportNode.setAttribute('aria-label', 'Image slideshow');

            slides.forEach(slide => {
                slide.setAttribute('role', 'group');
                slide.setAttribute('aria-roledescription', 'slide');
            });
        }

        buttons() {
            // Arrow buttons
            if (this.buttonPrev && this.buttonNext) {
                const scrollPrev = () => { this.embla.scrollPrev() };
                const scrollNext = () => { this.embla.scrollNext() };
                
                this.buttonPrev.addEventListener('click', scrollPrev, false);
                this.buttonNext.addEventListener('click', scrollNext, false);
        
                const togglePrevNextState = () => {
                    if (this.embla.canScrollPrev()) this.buttonPrev.removeAttribute('disabled')
                    else this.buttonPrev.setAttribute('disabled', 'disabled')

                    if (this.embla.canScrollNext()) this.buttonNext.removeAttribute('disabled')
                    else this.buttonNext.setAttribute('disabled', 'disabled')
                };

                this.embla
                    .on('select', togglePrevNextState)
                    .on('init', togglePrevNextState)
                    .on('reInit', togglePrevNextState);
        
                // Initialize button states
                togglePrevNextState();
                
                return () => {
                    this.buttonPrev.removeEventListener('click', scrollPrev, false);
                    this.buttonNext.removeEventListener('click', scrollNext, false);
                };
            }

            // Play button
            if (this.buttonPlay) {
                this.buttonPlay.addEventListener('click', () => {
                    if (this.autoplayPlugin) {
                        if (this.autoplayPlugin.isPlaying()) {
                            this.autoplayPlugin.stop();
                            this.buttonPlay.innerText = 'Play';
                        } else {
                            this.autoplayPlugin.start();
                            this.buttonPlay.innerText = 'Pause';
                        }
                    }
                });
            }
        }

        // Dots
        dots() {
            if (!this.buttonDots) return;

            // Clear existing dots
            this.buttonDots.innerHTML = '';

            // Get the number of slides
            const numberOfSlides = this.embla.slideNodes().length;

            // Create a dot for each slide
            for (let i = 0; i < numberOfSlides; i++) {
                const dot = document.createElement('button');
                dot.classList.add('control-dot'); // Add a class for styling
                dot.setAttribute('type', 'button');
                dot.setAttribute('aria-label', 'Go to next slide');
                dot.addEventListener('click', () => this.embla.scrollTo(i));
                this.buttonDots.appendChild(dot);
            }

            // Get the newly created dots
            const dots = Array.from(this.buttonDots.children);

            const updateDots = () => {
                const selectedIndex = this.embla.selectedScrollSnap();
                dots.forEach((dot, index) => {
                    dot.classList.toggle('is-selected', index === selectedIndex);

                });
            };

            this.embla.on('select', updateDots);
            updateDots();
        }

        // Numbers
        numbers() {
            if (!this.numbersNode) return;
            const updateNumbers = () => {
                const totalSlides = this.embla.scrollSnapList().length;
                const currentIndex = this.embla.selectedScrollSnap();
                this.numbersNode.innerText = `${currentIndex + 1} / ${totalSlides}`;
            };

            this.embla.on('select', updateNumbers).on('reInit', updateNumbers);
            updateNumbers();
        }

        // Autoplay
        stopAutoplay() {
            if (this.autoplayPlugin && this.autoplayPlugin.isPlaying()) this.autoplayPlugin.stop()
        }

        setupInteractionStopAutoplay() {
            const stopAutoplayOnInteraction = this.stopAutoplay.bind(this);

            const navElements = [this.buttonPrev, this.buttonNext, this.buttonDots, this.numbersNode];
            navElements.forEach(navElement => {
                if (navElement) navElement.addEventListener('click', stopAutoplayOnInteraction, true);
            });
        }

        selectSlide(event) {
            this.stopAutoplay(); // Stop autoplay

            // Get the slide index from the 'slider' attribute
            const slideIndex = event.target.getAttribute('slider-slide');

            // Ensure the slideIndex is a valid number
            if (slideIndex !== null) {
                const index = parseInt(slideIndex, 10) - 1; // Subtract 1 from the slide index to account for the difference in indexing
                if (!isNaN(index)) this.embla.scrollTo(index); // Scroll to the slide at the specified index
            }
        }

}